<template>
  <section id="dashboard" class="rk-preview">
    <div class="rk-container">
      <div class="rk-section__head">
        <div class="rk-kicker">
          <span class="rk-kicker__rule"></span>
          <span class="rk-kicker__text"><i class="bi bi-display"></i> See it in action</span>
        </div>
        <h2>Your entire portfolio, in one place</h2>
        <p>A look at the dashboard — portfolio overview, rent roll, and performance, all in real time.</p>
      </div>

      <!-- Panel 1: Portfolio overview -->
      <div class="rk-panel">
        <div class="rk-panel__visual" v-reveal="{ variant: 'left' }">
          <div class="rk-browser">
            <div class="rk-browser__bar">
              <span></span><span></span><span></span>
              <div class="rk-browser__url"><i class="bi bi-lock-fill"></i> app.rentalkenya.co.ke/portfolio</div>
            </div>
            <div class="rk-browser__body">
              <div class="rk-mock-stats">
                <div class="rk-mock-stat"><span>KES 2.4M</span><small>Collected this month</small></div>
                <div class="rk-mock-stat"><span>34</span><small>Occupied units</small></div>
                <div class="rk-mock-stat"><span>3</span><small>Vacant units</small></div>
              </div>
              <div class="rk-mock-list">
                <div class="rk-mock-row" v-for="n in 4" :key="n">
                  <span class="rk-mock-dot"></span>
                  <span class="rk-mock-bar" :style="{ width: `${40 + n * 12}%` }"></span>
                  <span class="rk-mock-pct">{{ 60 + n * 8 }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="rk-panel__copy" v-reveal="{ variant: 'right' }">
          <h3>Portfolio overview</h3>
          <p>Every property, every unit's occupancy, and this month's collections — the moment you log in, before you dig into any single tenant.</p>
        </div>
      </div>

      <!-- Panel 2: Rent roll & payments -->
      <div class="rk-panel rk-panel--reverse">
        <div class="rk-panel__copy" v-reveal="{ variant: 'left' }">
          <h3>Rent roll &amp; payments</h3>
          <p>Paid, due, and overdue — tracked automatically, with reminders sent before you'd have thought to chase anyone.</p>
        </div>
        <div class="rk-panel__visual" v-reveal="{ variant: 'right' }">
          <div class="rk-browser">
            <div class="rk-browser__bar">
              <span></span><span></span><span></span>
              <div class="rk-browser__url"><i class="bi bi-lock-fill"></i> app.rentalkenya.co.ke/rent-roll</div>
            </div>
            <div class="rk-browser__body">
              <table class="rk-mock-table">
                <thead>
                  <tr><th>Unit</th><th>Tenant</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  <tr><td>A1</td><td>J. Mwangi</td><td>15,000</td><td><span class="rk-tag rk-tag--paid">Paid</span></td></tr>
                  <tr><td>B2</td><td>F. Wanjiru</td><td>12,000</td><td><span class="rk-tag rk-tag--due">Due</span></td></tr>
                  <tr><td>C3</td><td>D. Otieno</td><td>10,000</td><td><span class="rk-tag rk-tag--overdue">Overdue</span></td></tr>
                  <tr><td>D4</td><td>M. Achieng</td><td>18,000</td><td><span class="rk-tag rk-tag--paid">Paid</span></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel 3: Analytics -->
      <div class="rk-panel">
        <div class="rk-panel__visual" v-reveal="{ variant: 'left' }">
          <div class="rk-browser">
            <div class="rk-browser__bar">
              <span></span><span></span><span></span>
              <div class="rk-browser__url"><i class="bi bi-lock-fill"></i> app.rentalkenya.co.ke/analytics</div>
            </div>
            <div class="rk-browser__body">
              <div class="rk-mock-chart">
                <div class="rk-mock-chart__bar" v-for="(h, i) in [40, 65, 50, 80, 70, 96]" :key="i" :style="{ height: `${h}%` }"></div>
              </div>
              <div class="rk-mock-chart__labels">
                <span v-for="m in ['May','Jun','Jul','Aug','Sep','Oct']" :key="m">{{ m }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="rk-panel__copy" v-reveal="{ variant: 'right' }">
          <h3>Real-time analytics</h3>
          <p>Collection trends, vacancy rates, and performance by property — so decisions are based on this month's numbers, not last quarter's memory.</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup></script>

<style scoped>
.rk-container { max-width: 1100px; margin: 0 auto; padding: 0 1.5rem; }
.rk-preview { padding: 6.5rem 0; background: var(--paper-dim); overflow: hidden; }



.rk-kicker {
  display: inline-flex; align-items: center; gap: 0.85rem;
  margin-bottom: 1rem;
}
.rk-kicker__rule { width: 32px; height: 3px; background: var(--brass); border-radius: 2px; flex-shrink: 0; }
.rk-kicker__text {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-mono); font-size: 0.95rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--brass);
}
.rk-kicker__text i { font-size: 1rem; }

.rk-section__head h2 {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 2.6rem;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.15;
}

.rk-section__head {
  max-width: 640px;
  margin-bottom: 80px;
}


.rk-panel {
  display: grid; grid-template-columns: 1.3fr 1fr; gap: 3.5rem;
  align-items: center; margin-bottom: 6rem;
}
.rk-panel:last-child { margin-bottom: 0; }
.rk-panel--reverse .rk-panel__copy { order: 1; }
.rk-panel--reverse .rk-panel__visual { order: 2; }

.rk-panel__copy h3 { font-family: var(--font-display); font-style: italic; font-size: 1.9rem; font-weight: 400; color: var(--ink); margin: 0 0 1rem; }
.rk-panel__copy p { font-family: var(--font-body); color: var(--slate); font-size: 1.02rem; line-height: 1.7; }

/* Browser chrome mockup */
.rk-browser {
  background: var(--paper); border-radius: var(--radius-lg); overflow: hidden;
  box-shadow: var(--shadow-card); border: 1px solid var(--line);
}
.rk-browser__bar { display: flex; align-items: center; gap: 0.4rem; padding: 0.85rem 1rem; background: var(--paper-dim); border-bottom: 1px solid var(--line); }
.rk-browser__bar span { width: 9px; height: 9px; border-radius: 50%; background: var(--line); }
.rk-browser__bar span:nth-child(1) { background: #E08363; }
.rk-browser__bar span:nth-child(2) { background: #E0B15C; }
.rk-browser__bar span:nth-child(3) { background: #7FBFA8; }
.rk-browser__url {
  margin-left: 0.75rem; font-family: var(--font-mono); font-size: 0.72rem; color: var(--slate-soft);
  background: var(--paper); padding: 0.3rem 0.7rem; border-radius: 100px; display: flex; align-items: center; gap: 0.35rem;
}
.rk-browser__body { padding: 1.75rem; }

/* Mock stat row */
.rk-mock-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.85rem; margin-bottom: 1.5rem; }
.rk-mock-stat { background: var(--neu-surface); box-shadow: var(--neu-raised-sm); border-radius: var(--radius-sm); padding: 1rem; text-align: center; }
.rk-mock-stat span { display: block; font-family: var(--font-mono); font-weight: 600; font-size: 1.1rem; color: var(--ink); }
.rk-mock-stat small { font-family: var(--font-body); font-size: 0.7rem; color: var(--slate-soft); }

.rk-mock-list { display: flex; flex-direction: column; gap: 0.65rem; }
.rk-mock-row { display: flex; align-items: center; gap: 0.75rem; }
.rk-mock-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--brass); flex-shrink: 0; }
.rk-mock-bar { height: 8px; border-radius: 4px; background: linear-gradient(90deg, var(--brass) 0%, var(--brass-light) 100%); }
.rk-mock-pct { font-family: var(--font-mono); font-size: 0.72rem; color: var(--slate-soft); margin-left: auto; }

/* Mock table */
.rk-mock-table { width: 100%; border-collapse: collapse; font-family: var(--font-body); font-size: 0.88rem; }
.rk-mock-table th { text-align: left; font-family: var(--font-mono); font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--slate-soft); padding-bottom: 0.75rem; border-bottom: 1px solid var(--line); }
.rk-mock-table td { padding: 0.75rem 0; border-bottom: 1px solid var(--line); color: var(--ink); }
.rk-mock-table tr:last-child td { border-bottom: none; }
.rk-tag { font-family: var(--font-mono); font-size: 0.7rem; padding: 0.25rem 0.6rem; border-radius: 100px; }
.rk-tag--paid { color: #3E6B58; background: rgba(79,122,107,0.15); }
.rk-tag--due { color: #93701F; background: rgba(192,138,46,0.15); }
.rk-tag--overdue { color: #A3431F; background: rgba(196,86,47,0.15); }

/* Mock chart */
.rk-mock-chart { display: flex; align-items: flex-end; gap: 0.85rem; height: 160px; padding: 0 0.5rem; }
.rk-mock-chart__bar { flex: 1; border-radius: 6px 6px 0 0; background: linear-gradient(180deg, var(--brass-light) 0%, var(--brass) 100%); }
.rk-mock-chart__labels { display: flex; gap: 0.85rem; padding: 0.75rem 0.5rem 0; }
.rk-mock-chart__labels span { flex: 1; text-align: center; font-family: var(--font-mono); font-size: 0.68rem; color: var(--slate-soft); }

@media (max-width: 900px) {
  .rk-panel, .rk-panel--reverse { grid-template-columns: 1fr; gap: 2rem; }
  .rk-panel--reverse .rk-panel__copy, .rk-panel--reverse .rk-panel__visual { order: 0; }
  .rk-panel { margin-bottom: 3.5rem; }
}
@media (max-width: 560px) {
  .rk-preview { padding: 4.5rem 0; }
  .rk-preview__head h2 { font-size: 1.9rem; }
  .rk-mock-stats { grid-template-columns: 1fr; }
}
</style>